import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import LoginModal from '../../components/feature/LoginModal';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tracking.ibxshipping.com';

  // SEO Configuration
  useSEO({
    title: 'IBX Shipping - Global Logistics & Shipment Tracking Services',
    description: 'IBX Shipping provides professional global logistics and real-time shipment tracking services. Track your cargo from Lagos to Malta and worldwide destinations with secure, reliable shipping solutions.',
    keywords: 'IBX Shipping, shipment tracking, global logistics, cargo shipping, freight services, international shipping, Lagos shipping, Malta delivery',
    canonical: `${siteUrl}/`,
    ogType: 'website',
    ogImage: `${siteUrl}/og-image.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "IBX Shipping",
          "description": "Professional global logistics and shipment tracking services",
          "publisher": {
            "@id": `${siteUrl}/#organization`
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/track?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          "name": "IBX Shipping",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": `${siteUrl}/logo.png`,
            "width": 250,
            "height": 60
          },
          "description": "Professional global logistics and shipment tracking services",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Lagos",
            "addressCountry": "NG"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "availableLanguage": "English"
          },
          "sameAs": [
            "https://ibxshipping.com"
          ]
        },
        {
          "@type": "Service",
          "serviceType": "Logistics and Shipping",
          "provider": {
            "@id": `${siteUrl}/#organization`
          },
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Shipping Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Ocean Freight",
                  "description": "Cost-effective shipping for large cargo volumes with full container load (FCL) and less than container load (LCL) options"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Air Freight",
                  "description": "Fast delivery for time-sensitive shipments with express and standard air cargo services worldwide"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Ground Transport",
                  "description": "Reliable land transportation with full truckload (FTL) and less than truckload (LTL) shipping solutions"
                }
              }
            ]
          }
        }
      ]
    }
  });

  const handleLogin = (user: any) => {
    // Store user info and redirect to admin dashboard
    localStorage.setItem('currentUser', JSON.stringify(user));
    setShowLoginModal(false);
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onLoginClick={() => setShowLoginModal(true)} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20cargo%20ship%20sailing%20through%20calm%20ocean%20waters%20at%20sunset%20with%20containers%20stacked%20on%20deck%2C%20professional%20maritime%20logistics%2C%20deep%20blue%20ocean%2C%20golden%20horizon%2C%20commercial%20shipping%20vessel%2C%20international%20trade%2C%20supply%20chain%20management%2C%20peaceful%20seascape%2C%20industrial%20maritime%20scene&width=1920&height=1080&seq=hero1&orientation=landscape')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Global Shipping
                <span className="block text-blue-300">Made Simple</span>
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Track your shipments in real-time with our advanced logistics platform. 
                From pickup to delivery, we ensure your cargo reaches its destination safely and on time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/track')}
                  className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap"
                >
                  Track Shipment
                </button>
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-colors whitespace-nowrap"
                >
                  Staff Login
                </button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20shipping%20container%20terminal%20with%20cranes%20loading%20cargo%20onto%20massive%20container%20ship%2C%20modern%20port%20facility%2C%20organized%20logistics%20operations%2C%20blue%20and%20orange%20containers%2C%20industrial%20maritime%20infrastructure%2C%20efficient%20cargo%20handling%2C%20global%20trade%20hub%2C%20commercial%20shipping%20port&width=600&height=400&seq=hero2&orientation=landscape"
                alt="Shipping Operations"
                className="rounded-lg shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose IBX Shipping?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive logistics solutions with cutting-edge technology 
              and unmatched customer service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-global-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Global Network</h3>
              <p className="text-gray-600 leading-relaxed">
                Access to worldwide shipping routes with partnerships across 150+ countries. 
                Your cargo reaches any destination efficiently.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Real-Time Tracking</h3>
              <p className="text-gray-600 leading-relaxed">
                Monitor your shipments 24/7 with GPS tracking, automated updates, 
                and detailed delivery confirmations.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Secure & Insured</h3>
              <p className="text-gray-600 leading-relaxed">
                Full insurance coverage and secure handling protocols ensure your 
                valuable cargo is protected throughout transit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Shipping Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From small packages to large cargo containers, we handle all your shipping needs 
              with professional care and efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20warehouse%20with%20organized%20shipping%20containers%20and%20logistics%20equipment%2C%20professional%20cargo%20handling%20facility%2C%20efficient%20storage%20systems%2C%20industrial%20shipping%20operations%2C%20clean%20organized%20space%2C%20blue%20and%20white%20containers%2C%20automated%20logistics%20technology%2C%20supply%20chain%20management&width=600&height=400&seq=services1&orientation=landscape"
                alt="Warehouse Operations"
                className="rounded-lg shadow-lg object-cover w-full h-80"
              />
            </div>
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-ship-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Ocean Freight</h3>
                  <p className="text-gray-600">
                    Cost-effective shipping for large cargo volumes with full container load (FCL) 
                    and less than container load (LCL) options.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-plane-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Air Freight</h3>
                  <p className="text-gray-600">
                    Fast delivery for time-sensitive shipments with express and standard 
                    air cargo services worldwide.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-truck-line text-purple-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Ground Transport</h3>
                  <p className="text-gray-600">
                    Reliable land transportation with full truckload (FTL) and less than 
                    truckload (LTL) shipping solutions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-300 mb-2">25+</div>
              <div className="text-lg text-blue-100">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-300 mb-2">150+</div>
              <div className="text-lg text-blue-100">Countries Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-300 mb-2">50K+</div>
              <div className="text-lg text-blue-100">Shipments Delivered</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-300 mb-2">99.8%</div>
              <div className="text-lg text-blue-100">On-Time Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Ship with IBX?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get started today and experience the difference of professional logistics management. 
            Track your shipments and manage your cargo with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/track')}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Track Your Shipment
            </button>
            <button 
              onClick={() => setShowLoginModal(true)}
              className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors whitespace-nowrap"
            >
              Staff Access
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
      />
    </div>
  );
}
